'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Menu, Button, Tooltip } from '@mantine/core';

import { Icon } from '@/elements/icon/icon.component';
import {
  Container,
  TitleWrap,
  Title,
  Features,
  Feature,
  Toggles,
  PriceData,
  OldPrice,
  CurrentPrice,
  Price,
  PriceAddition,
  Discount,
  ActionButton,
  Ribbon,
  Section,
  HorizontalContainer,
} from './plan-card.elements';

import { useAuth } from '@/providers/auth.provider';
import { pluralizeDan, pluralizeRata } from '@/helpers/pluralize.helpers';

import { IconCheck, IconX, IconInfoCircle } from '@tabler/icons-react';

const PlanCard = ({
  plan: {
    id: planId,
    title,
    isHybrid,
    type: planType,
    features: { bullets, ribbon: planRibbon, ribbonExpiresAt: planRibbonExpiresAt },
    educationDetails,
    subscriptionDetails,
  },
  education,
  selected,
  registration,
  onClick,
  dynamic: parentDynamic,
  horizontal,
  actionType,
}) => {
  const [dynamic, setDynamic] = useState(parentDynamic); // total | monthly | null
  const [type, setType] = useState(isHybrid ? 'live' : null); // live | online | null
  const [discount, setDiscount] = useState(0);

  const { user } = useAuth();
  const router = useRouter();

  const ribbon =
    planType === 'subscription'
      ? planRibbon
      : educationDetails?.ribbon
        ? !educationDetails.ribbonExpiresAt ||
          new Date(educationDetails.ribbonExpiresAt) > new Date()
          ? educationDetails.ribbon
          : planRibbon
        : educationDetails?.maxRegistrations
          ? `Još ${educationDetails?.maxRegistrations - educationDetails.registrationCount} ${educationDetails?.maxRegistrations - educationDetails.registrationCount === 1 ? 'mjesto' : 'mjesta'}!`
          : null;

  const ribbonExpiresAt =
    (educationDetails?.ribbon || educationDetails?.maxRegistrations) &&
    (!educationDetails.ribbonExpiresAt || new Date(educationDetails.ribbonExpiresAt) > new Date())
      ? educationDetails.ribbonExpiresAt
      : planRibbonExpiresAt;

  const hasRibbon =
    ribbon?.length > 0 && (!ribbonExpiresAt || new Date(ribbonExpiresAt) > new Date());

  const determineDuration = () => {
    // Minimum length is 30 days, extra 10 days per module
    const duration = 30 + (education.modules.length - 1) * 10;

    return duration;
  };

  useEffect(() => {
    if (
      !registration ||
      (actionType !== 'gift-education' &&
        (planType === 'subscription' || educationDetails?.installmentsAllowed))
    ) {
      setDynamic('monthly');
    } else {
      setDynamic('total');
    }
  }, []);

  useEffect(() => {
    if (calculatePrice(discount) === 0) {
      setDynamic('total');
    }
  }, [discount]);

  useEffect(() => {
    if (planType === 'subscription') {
      // Education pages share the toggle: their `total` means annual for subscriptions.
      setDynamic(parentDynamic === 'monthly' ? 'monthly' : 'annual');
    } else if (educationDetails?.installmentsAllowed && parentDynamic === 'monthly') {
      setDynamic('monthly');
    } else {
      setDynamic('total');
    }
  }, [parentDynamic, planType, educationDetails?.installmentsAllowed]);

  useEffect(() => {
    const discountExpiresAt =
      educationDetails?.discountExpiresAt || subscriptionDetails?.discountExpiresAt || null;
    const isDiscountValid = discountExpiresAt ? new Date(discountExpiresAt) > new Date() : true;
    const availableDiscount = educationDetails?.discount || subscriptionDetails?.discount || 0;
    const discount = isDiscountValid ? availableDiscount : 0;

    if (
      user?.alumni &&
      planType !== 'subscription' &&
      education.type !== 'webinar' &&
      !registration &&
      actionType !== 'gift-education'
    ) {
      setDiscount(Math.max(discount, process.env.ALUMNI_DISCOUNT));
    } else setDiscount(discount);
  }, [user]);

  const calculatePrice = (discount = 0) => {
    if (!registration || actionType === 'gift-education' || registration.type === 'subscription') {
      if (planType === 'subscription') {
        const {
          priceMonthly,
          priceAnnual,
          priceMonthlyLive,
          priceAnnualLive,
          priceMonthlyOnline,
          priceAnnualOnline,
        } = subscriptionDetails;

        const price = !type
          ? dynamic === 'monthly'
            ? priceMonthly
            : priceAnnual
          : type === 'live'
            ? dynamic === 'monthly'
              ? priceMonthlyLive
              : priceAnnualLive
            : dynamic === 'monthly'
              ? priceMonthlyOnline
              : priceAnnualOnline;

        return (price * (100 - discount)) / 100;
      } else {
        const {
          differentOnlineLivePricing,
          priceMonthlyLive,
          priceLive,
          priceMonthlyOnline,
          priceOnline,
          priceMonthly,
          priceTotal,
        } = educationDetails;

        if (differentOnlineLivePricing) {
          const price =
            type === 'live'
              ? dynamic === 'monthly'
                ? priceMonthlyLive
                : priceLive
              : dynamic === 'monthly'
                ? priceMonthlyOnline
                : priceOnline;

          return (price * (100 - discount)) / 100;
        }

        return ((dynamic === 'monthly' ? priceMonthly : priceTotal) * (100 - discount)) / 100;
      }
    } else {
      if (planType === 'subscription') {
        const {
          priceMonthly,
          priceAnnual,
          priceMonthlyLive,
          priceAnnualLive,
          priceMonthlyOnline,
          priceAnnualOnline,
        } = subscriptionDetails;

        const price = !type
          ? dynamic === 'monthly'
            ? priceMonthly
            : priceAnnual
          : type === 'live'
            ? dynamic === 'monthly'
              ? priceMonthlyLive
              : priceAnnualLive
            : dynamic === 'monthly'
              ? priceMonthlyOnline
              : priceAnnualOnline;

        return (price * (100 - discount)) / 100;
      } else {
        const { differentOnlineLivePricing, priceLive, priceOnline, priceTotal } = educationDetails;
        const { payments, validFrom, expiresAt } = registration;
        const totalPaid = payments.reduce((acc, payment) => acc + payment.amount, 0);
        const originalDuration = Math.ceil(
          Math.abs(new Date(expiresAt) - new Date(validFrom)) / (1000 * 60 * 60 * 24)
        );
        const daysToExpiration = Math.ceil(
          Math.abs(new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24)
        );

        const planPrice = differentOnlineLivePricing
          ? type === 'live'
            ? (priceLive * (100 - discount)) / 100
            : (priceOnline * (100 - discount)) / 100
          : (priceTotal * (100 - discount)) / 100;

        if (totalPaid >= planPrice) {
          return 0;
        } else {
          return planPrice - totalPaid * (daysToExpiration / originalDuration);
        }
      }
    }
  };

  const proceedWithPurchase = () => {
    if (selected || horizontal) {
      // Only identity + user choices go into the URL — pricing, features and
      // installment details are resolved server-side on the prijava page
      const data = {
        plan: {
          id: planId,
        },
        payment: {
          dynamic,
        },
        actionType,
      };

      if (isHybrid) {
        data.attendance = type;
      }

      if (education) {
        data.educationId = education.id;
        data.slug = education.slug;
        data.nextLectureTime = education.nextLectureTime;
      } else {
        data.slug = 'pretplata';
      }

      if (registration && actionType !== 'gift-education') {
        data.upgradedRegistration = {
          id: registration.id,
        };
      }

      const searchParams = new URLSearchParams();

      // Add all properties from data object to searchParams
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && typeof value === 'object') {
          Object.entries(value).forEach(([subKey, subValue]) => {
            searchParams.append(`${key}.${subKey}`, String(subValue));
          });
        } else {
          searchParams.append(key, String(value));
        }
      });

      // Navigate to the new route with search params
      router.push(`/prijava-na-edukaciju?${searchParams.toString()}`);
    }
  };

  // Education plans
  if (horizontal) {
    return (
      <>
        <HorizontalContainer onClick={onClick}>
          {hasRibbon ? (
            <Ribbon $color={educationDetails?.maxRegistrations ? 'purple' : 'aqua'}>
              <span>{ribbon}</span>
            </Ribbon>
          ) : null}

          <Section>
            <TitleWrap $horizontal={horizontal}>
              <Title $withRibbon={hasRibbon}>{title}</Title>
            </TitleWrap>

            <PriceData $horizontal={horizontal}>
              {calculatePrice(discount) > 0 && discount > 0 ? (
                <OldPrice>
                  <Price>
                    {new Intl.NumberFormat('hr', {
                      style: 'currency',
                      currency: 'EUR',
                    })
                      .format(calculatePrice())
                      .replace(/\D00(?=\D*$)/, '')}
                  </Price>
                  {discount > 0 ? <Discount>-{discount}%</Discount> : null}
                </OldPrice>
              ) : null}
              <CurrentPrice>
                <Price>
                  {calculatePrice(discount) > 0
                    ? new Intl.NumberFormat('hr', {
                        style: 'currency',
                        currency: 'EUR',
                      })
                        .format(calculatePrice(discount))
                        .replace(/\D00(?=\D*$)/, '')
                    : 'Besplatno'}
                </Price>
                {education && dynamic === 'monthly' && calculatePrice(discount) > 0 ? (
                  <PriceAddition $selected={selected}>
                    <p>x {educationDetails?.installmentsNumber}</p>
                    <p>{pluralizeRata(educationDetails?.installmentsNumber)}</p>
                  </PriceAddition>
                ) : null}
              </CurrentPrice>
            </PriceData>

            <ActionButton width="100%" background="light" onClick={proceedWithPurchase}>
              {!registration || actionType === 'gift-education'
                ? 'Odaberi ovaj paket'
                : 'Nadogradi'}
            </ActionButton>
          </Section>

          <Section>
            <Features $horizontal={horizontal}>
              {bullets
                .filter((bullet) => bullet.label)
                .map((bullet) => (
                  <Feature key={bullet.id} $has={bullet.contains}>
                    <Icon>{bullet.contains ? <IconCheck size={22} /> : <IconX size={22} />}</Icon>
                    {bullet.label}
                    {bullet.label === 'Dostupna snimka ove edukacije' && determineDuration()
                      ? ` (${determineDuration()} ${pluralizeDan(determineDuration())})`
                      : ''}
                    {bullet.note?.length > 0 ? (
                      <Icon>
                        <Tooltip
                          label={bullet.note}
                          events={{ hover: true, focus: true, touch: true }}
                          withArrow
                          transitionProps={{ duration: 200 }}
                          color="blue"
                          multiline
                          w={256}
                        >
                          <IconInfoCircle size={22} />
                        </Tooltip>
                      </Icon>
                    ) : null}
                  </Feature>
                ))}
            </Features>
          </Section>
        </HorizontalContainer>
      </>
    );
  }

  // Subscription plans
  return (
    <Container onClick={onClick} $selected={selected}>
      {hasRibbon ? (
        <Ribbon $color={educationDetails?.maxRegistrations ? 'purple' : 'aqua'}>
          <span>{ribbon}</span>
        </Ribbon>
      ) : null}
      <TitleWrap>
        {/* <PlanType>{planType === 'education' ? 'Edukacija' : 'Pretplata'}</PlanType> */}
        <Title $withRibbon={hasRibbon}>{title}</Title>
      </TitleWrap>
      <Features>
        {bullets
          .filter((bullet) => bullet.label)
          .map((bullet) => (
            <Feature key={bullet.id} $has={bullet.contains}>
              <Icon>{bullet.contains ? <IconCheck size={22} /> : <IconX size={22} />}</Icon>
              {bullet.label}
              {bullet.note?.length > 0 ? (
                <Icon>
                  <Tooltip
                    label={bullet.note}
                    events={{ hover: true, focus: true, touch: true }}
                    withArrow
                    transitionProps={{ duration: 200 }}
                    color="blue"
                    multiline
                    w={256}
                  >
                    <IconInfoCircle size={22} />
                  </Tooltip>
                </Icon>
              ) : null}
            </Feature>
          ))}
      </Features>
      <Toggles>
        {isHybrid ? (
          <>
            <Menu
              offset={0}
              width={200}
              shadow="sm"
              transitionProps={{ transition: 'fade-up', duration: 150 }}
              style={{ width: 110 }}
              position="top"
            >
              <Menu.Target>
                <Button
                  onClick={() => setType('live')}
                  size="sm"
                  width="100%"
                  variant={type === 'live' ? 'filled' : 'default'}
                >
                  Uživo
                </Button>
              </Menu.Target>
            </Menu>

            <Menu
              offset={0}
              width={200}
              shadow="sm"
              transitionProps={{ transition: 'fade-up', duration: 150 }}
              style={{ width: 110 }}
              position="top"
            >
              <Menu.Target>
                <Button
                  onClick={() => setType('online')}
                  size="sm"
                  width="100%"
                  variant={type === 'online' ? 'filled' : 'default'}
                >
                  Online
                </Button>
              </Menu.Target>
            </Menu>
          </>
        ) : null}
      </Toggles>
      <PriceData>
        {calculatePrice(discount) > 0 && discount > 0 ? (
          <OldPrice>
            <Price>
              {new Intl.NumberFormat('hr', {
                style: 'currency',
                currency: 'EUR',
              })
                .format(calculatePrice())
                .replace(/\D00(?=\D*$)/, '')}
            </Price>
            {discount > 0 ? <Discount>-{discount}%</Discount> : null}
          </OldPrice>
        ) : null}
        <CurrentPrice>
          <Price>
            {calculatePrice(discount) > 0
              ? new Intl.NumberFormat('hr', {
                  style: 'currency',
                  currency: 'EUR',
                })
                  .format(calculatePrice(discount))
                  .replace(/\D00(?=\D*$)/, '')
              : 'Besplatno'}
          </Price>
          {education && dynamic === 'monthly' && calculatePrice(discount) > 0 ? (
            <PriceAddition $selected={selected}>
              <p>{educationDetails?.installmentsNumber} mj.</p>
              <p>{pluralizeRata(educationDetails?.installmentsNumber)}</p>
            </PriceAddition>
          ) : null}
        </CurrentPrice>
      </PriceData>
      <ActionButton
        width="100%"
        background="light"
        onClick={proceedWithPurchase}
        style={{ opacity: selected ? 1 : 0.4 }}
      >
        {!registration || actionType === 'gift-education' ? 'Odaberi ovaj paket' : 'Nadogradi'}
      </ActionButton>
    </Container>
  );
};

export { PlanCard };
